import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/store';
import classes from './Customers.module.css';
import { fetchGenders } from '../../store/slices/gender';
import { fetchCountries } from '../../store/slices/countries';
import { ICustomer, fetchCustomers, selectCustomers } from '../../store/slices/customers';
import { ReportTable } from '../../components/UI/Layouts/ReportTable';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import { AddCustomer } from './AddCustomer';
import { CustomerView } from './CustomerView';

import { client } from '../../networking/client';
import { IDirtyState, selectDirtyState, setDirtyState } from '../../store/slices/auth';
import { ReportsTableHeader } from '../../components/Headers/ReportsTableHeader';
import { format } from 'date-fns';
import { fetchSalesOrders } from '../../store/slices/salesOrders';
import leftArrow from './../../assets/icons/backIcon.svg';
import { getAccountNumberFromEbaasPersonAccounts } from '../../utils/miscUtils';
import CustomPagination from '../../components/UI/CustomPagination/CustomPagination';
import { useTranslation } from '../../hooks/useTranslations';

const Customer: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const customers: ICustomer[] = useAppSelector<ICustomer[]>(selectCustomers);
  const { translations } = useTranslation();

  const [isAddingPerson, setIsAddingPerson] = useState<boolean>(false);
  const [displayCustomers, setDisplayCustomers] = useState<ICustomer[]>([]);
  const [nameSearch, setNameSearch] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer>();

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCountries());
    dispatch(fetchGenders());
    dispatch(fetchSalesOrders());
  }, [dispatch]);

  useEffect(() => {
    setDisplayCustomers([...customers]);
  }, [customers]);

  useEffect(() => {
    const addAccountNumberToSelectedCustomer = async (customer: ICustomer) => {
      // adding 3 seconds delay because account number isn't created immediately after user is registered
      await new Promise((resp) => setTimeout(resp, 3000));
      const ebaasPersonInfo = await client.get(
        `/core/person/accounts/${customer?.person_external_id}`
      );
      const accountNumberFetched = getAccountNumberFromEbaasPersonAccounts(ebaasPersonInfo);
      const response = await client.put(
        `/healthpoint/person/accountNumber/${customer?.person_external_id}/${accountNumberFetched}`
      );
      if (response.data?.person) {
        setSelectedCustomer(undefined);
        await new Promise((res) => setTimeout(res, 200));
        setSelectedCustomer({
          ...customer,
          person_account_number: response.data?.person.person_account_number,
        });
      }
    };
    if (selectedCustomer && !selectedCustomer.person_account_number) {
      addAccountNumberToSelectedCustomer(selectedCustomer);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    let tempoCustomers = [...customers];
    const searchValue = nameSearch.trim().toLowerCase();

    if (searchValue.length > 0) {
      tempoCustomers = tempoCustomers.filter((customer) => {
        const fullName = `${customer.person_firstname} ${customer.person_lastname}`.toLowerCase();
        const dob = customer.person_birthdate
          ? format(new Date(customer.person_birthdate), 'dd.MM.yyyy').toLowerCase()
          : '';
        const email = customer.person_email?.toLowerCase() || '';

        return (
          fullName.includes(searchValue) ||
          dob.includes(searchValue) ||
          email.includes(searchValue)
        );
      });
    }

    setDisplayCustomers([...tempoCustomers]);
  }, [customers, nameSearch]);

  const isDirtyModal = useAppSelector<IDirtyState>(selectDirtyState);

  const handleBackButton = () => {
    if (!isDirtyModal.isDirty) {
      setIsAddingPerson(false);
      setSelectedCustomer(undefined);
    } else {
      dispatch(setDirtyState({ isDirty: true, isModalOpen: true }));
    }
  };

  return (
    <div className={classes.mainContainer}>
      {(isAddingPerson || selectedCustomer) && (
        <div className={classes.mainHeader}>
          <div className={classes.headerRegister}>
            <div className={classes.customerstext}>{translations('customers')}</div>
          </div>
          <div onClick={handleBackButton} className={classes.backHeader}>
            <img src={leftArrow} alt="leftArrow" />
            <div className={classes.backLabel}>{translations('back')}</div>
          </div>
        </div>
      )}
      <div className={classes.container}>
        {isAddingPerson ? (
          <AddCustomer setIsAddingPerson={setIsAddingPerson} />
        ) : (
          <>
            {!selectedCustomer && (
              <div>
                <div className={classes.headerContainer}>
                  <div className={classes.boldLabel}>{translations('customers')}</div>
                  <PrimaryButtonV2
                    text={translations('button.register')}
                    containerStyle={{}}
                    buttonStyle={{
                      height: 44,
                      borderRadius: 7,
                    }}
                    disabled={false}
                    onClick={() => {
                      setIsAddingPerson(true);
                    }}
                  />
                </div>
                <div style={{ padding: '10px' }}>
                  <ReportsTableHeader
                    tabs={[]}
                    firstNameSearch={nameSearch}
                    setFirstNameSearch={setNameSearch}
                    showExportButton={true}
                    dataToExport={customers.map((data) => {
                      return {
                        person_id: data.person_id,
                        person_firstname: data.person_firstname,
                        person_lastname: data.person_lastname,
                        gender: data.gender,
                        person_birthdate: format(new Date(data.person_birthdate), 'dd.MM.yyyy'),
                        person_email: data.person_email,
                        person_mobile_phone: data.person_mobile_phone,
                        street: data.street,
                        house_number: data.house_number,
                        city: data.city,
                        postcode: data.postcode,
                        country: data.country,
                        person_datetime_created: format(
                          new Date(data.person_datetime_created),
                          'dd.MM.yyyy'
                        ),
                      };
                    })}
                  />
                  <CustomPagination
                    itemsPerPage={10}
                    items={displayCustomers.sort((customerA, customerB) => {
                      if (customerA?.person_id && customerB?.person_id) {
                        return customerB.person_id - customerA.person_id;
                      }
                      return -1;
                    })}
                  >
                    {(paginatedItems: any[]) => (
                      <ReportTable
                        columns={[
                          {
                            name: translations('first_name'),
                            path: 'person_firstname',
                            type: 'TEXT',
                          },
                          {
                            name: translations('last_name'),
                            path: 'person_lastname',
                            type: 'TEXT',
                          },
                          {
                            name: translations('date_of_birth'),
                            path: 'person_birthdate',
                            type: 'DATE',
                          },
                          {
                            name: translations('gender'),
                            path: 'gender',
                            type: 'GENDER',
                          },
                          {
                            name: translations('mobile'),
                            path: 'person_mobile_phone',
                            type: 'TEXT',
                          },
                          {
                            name: translations('email'),
                            path: 'person_email',
                            type: 'TEXT',
                          },
                          {
                            name: translations('date_of_registration'),
                            path: 'person_datetime_created',
                            type: 'DATE',
                          },
                        ]}
                        rows={paginatedItems}
                        actions={[
                          {
                            displayName: translations('view'),
                            onClick: (row) => {
                              setSelectedCustomer(row as ICustomer);
                            },
                          },
                        ]}
                      />
                    )}
                  </CustomPagination>
                </div>
              </div>
            )}

            {selectedCustomer && (
              <CustomerView
                isOpen={!!selectedCustomer}
                onClose={() => setSelectedCustomer(undefined)}
                customer={selectedCustomer}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Customer;
